import React from "react";
import { Grid, Tooltip } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import AcxClickableCell from "components/UI/AcxDataGrid/AcxClickableCell";
import AcxChip from "components/UI/AcxChip";
import AcxProgress from "components/UI/AcxDataGrid/Formatters/AcxProgress";
import { SignalsTrendingIndicator } from "../SignalsTrendingIndicator";
import theme from "Theme/AppTheme";
import type IColDef from "components/UI/AcxDataGrid/IColDef";
import type { SignalsTableColumn } from "../../SignalsModels";
import type TableCardStore from "./TableCardStore";
import { Link } from "react-router-dom";
import moment from "moment";

/**
 * Context passed into formatter factories so interactive formatters
 * can call store methods directly from the cell.
 */
export type FormatterContext = {
    store: TableCardStore;
};

type FormatterFactory = (
    opts: Record<string, unknown>,
    ctx: FormatterContext,
) => (params: GridRenderCellParams) => React.ReactNode;

const formatterRegistry: Record<string, FormatterFactory> = {
    topicDrilldownCell: (_opts, ctx) => (params) =>
        (
            <AcxClickableCell
                onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    const selection = ctx.store.selectTopicRow(params.row);
                    if (selection) {
                        ctx.store.openPointMenu({
                            top: e.clientY,
                            left: e.clientX,
                        });
                    }
                }}
            >
                {params.value}
            </AcxClickableCell>
        ),

    // Same interaction as topicDrilldownCell, but resolves a Driver selection instead of a Topic
    // one. Registered under its own name so the backend column contract says which it means.
    driverDrilldownCell: (_opts, ctx) => (params) =>
        (
            <AcxClickableCell
                onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    const selection = ctx.store.selectDriverRow(params.row);
                    if (selection) {
                        ctx.store.openPointMenu({
                            top: e.clientY,
                            left: e.clientX,
                        });
                    }
                }}
            >
                {params.value}
            </AcxClickableCell>
        ),

    chip: (opts) => (params) => {
        const value = params.value?.toString();
        if (!value) return null;
        return (
            <Grid item container style={{ height: "100%" }} alignItems="center">
                <AcxChip
                    size={(opts.size as "small" | "medium") ?? "small"}
                    color={(opts.color as any) ?? "gray"}
                    label={value}
                />
            </Grid>
        );
    },

    trendIndicator: (opts) => (params) => {
        const cell = params.value as
            | { value: number; isNew?: boolean; isToZero?: boolean }
            | undefined;
        if (!cell) return null;
        return (
            <SignalsTrendingIndicator
                inputValue={Number(cell.value)}
                isNew={cell.isNew ?? false}
                isToZero={cell.isToZero ?? false}
                isNegativeRed={(opts.isNegativeRed as boolean) ?? false}
            />
        );
    },

    progressBar: (opts) => (params) =>
        (
            <Grid
                container
                item
                xs={12}
                wrap="nowrap"
                justifyContent="center"
                alignItems="center"
                gap={1}
            >
                <Grid item xs={10}>
                    <AcxProgress
                        key={params.id}
                        percentComplete={params.value?.toString()}
                        backgroundColor={
                            (opts.color as string) ?? theme.palette.primary.main
                        }
                    />
                </Grid>
                {opts.showLabel !== false && (
                    <Grid item xs={2}>
                        {params.value}%
                    </Grid>
                )}
            </Grid>
        ),

    link: () => (params) => {
        const cell = params.value as
            | { label: string; path: string }
            | undefined;
        if (!cell?.path) return null;
        return <Link to={cell.path}>{cell.label}</Link>;
    },

    date: () => (params) => {
        const value = params.value?.toString();
        const date = moment.utc(value).local();
        return date.format("YYYY-MM-DD");
    },

    /**
     * Renders object/array cell values as a single-line JSON preview that, when
     * clicked, opens a dialog showing the full pretty-formatted value. Prevents
     * objects from falling through to MUI's default "[object Object]" rendering.
     */
    json: (_opts, ctx) => (params) => {
        const value = params.value;
        if (value === null || value === undefined) return null;
        if (typeof value !== "object") return value;

        const preview = JSON.stringify(value);
        return (
            <Tooltip
                title="Selecting this will open a full JSON preview"
                enterDelay={500}
                enterNextDelay={250}
            >
                <AcxClickableCell
                    onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        ctx.store.openJsonCellViewer(
                            value,
                            params.colDef.headerName ?? params.field,
                        );
                    }}
                    sx={{ textUnderlineOffset: 4 }}
                >
                    {preview}
                </AcxClickableCell>
            </Tooltip>
        );
    },

    default: (opts, ctx) => (params) => {
        // fall back to the json formatter so that any
        // object/array cell value renders as a readable, clickable JSON preview
        // instead of MUI's default "[object Object]". Primitives pass through.
        const value = params.value;
        if (value !== null && typeof value === "object") {
            const jsonRenderCell = formatterRegistry.json(opts, ctx);
            return jsonRenderCell(params);
        }
        return value as React.ReactNode;
    },
};

/**
 * Converts a backend SignalsTableColumn into an MUI DataGrid IColDef,
 * resolving the formatter string to a renderCell implementation.
 */
export function toGridColDef(
    col: SignalsTableColumn,
    ctx: FormatterContext,
): IColDef {
    const base: IColDef = {
        field: col.field,
        headerName: col.headerName,
        flex: col.flex,
        width: col.width,
        sortable: col.sortable ?? true,
        align: col.align,
        headerAlign: col.headerAlign,
        hide: col.hide,
    };

    if (
        col.formatter &&
        col.formatter !== "none" &&
        formatterRegistry[col.formatter]
    ) {
        base.renderCell = formatterRegistry[col.formatter](
            col.formatterOptions ?? {},
            ctx,
        );
    } else {
        base.renderCell = formatterRegistry.default(
            col.formatterOptions ?? {},
            ctx,
        );
    }

    return base;
}

export default formatterRegistry;
